import { create } from "zustand";
import { emitTo, listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import {
  getCurrentOwnerEpoch,
  getCurrentOwnerLabel,
  getMyWindowLabel,
  subscribeOwnerChanged,
} from "@/lib/owner-state-client";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
  type DesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import {
  isBrowserId,
  parseBrowserConnectionStatus,
  parseBrowserSetupReport,
  type BrowserConnectionStatus,
  type BrowserId,
  type BrowserSetupReport,
} from "./browser-connection";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import { translate } from "@/i18n";
import { desktopBrowserMessages } from "@/i18n/messages/desktop-browser";

export const BROWSER_SESSION_ACTION = "denker://browser-session-action";
export const BROWSER_SESSION_STATE = "denker://browser-session-state";
export const BROWSER_SESSION_REQUEST = "denker://browser-session-request";
export type BrowserSessionAction =
  | "connect"
  | "restart-connect"
  | "check"
  | "disconnect"
  | "guide"
  | "stop";
export type BrowserConnectionSession = {
  id: string;
  browserId: BrowserId;
  setupId: string | null;
  executionId?: string;
  status: BrowserConnectionStatus | null;
  attempt: {
    report: BrowserSetupReport;
    setupId: string;
    completedAt: number;
  } | null;
  busy: "setup" | "check" | "guide" | "disconnect" | null;
  error: string | null;
  /** What `error` says, for code: the text itself is translated. */
  errorKind?: BrowserSessionErrorKind | null;
  guideLabel: string | null;
  yielding: boolean;
  yieldId?: string;
};

const ERROR_KEYS = {
  "setup-stopped": "errorSetupStopped",
  "setting-unidentified": "errorSettingUnidentified",
  "setting-not-found": "errorSettingNotFound",
  "connection-unfinished": "errorConnectionUnfinished",
} as const;
export type BrowserSessionErrorKind = keyof typeof ERROR_KEYS;
const isErrorKind = (value: unknown): value is BrowserSessionErrorKind =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(ERROR_KEYS, value);

/** A session error people read, with the kind code recognises it by. */
export function browserSessionError(
  kind: BrowserSessionErrorKind,
): Pick<BrowserConnectionSession, "error" | "errorKind"> {
  return {
    error: translate(desktopBrowserMessages, ERROR_KEYS[kind]),
    errorKind: kind,
  };
}

/** Applies a patch; an error set without a kind drops the previous kind. */
export function patchBrowserSession(
  session: BrowserConnectionSession,
  patch: Partial<BrowserConnectionSession>,
): BrowserConnectionSession {
  return "error" in patch && !("errorKind" in patch)
    ? { ...session, ...patch, errorKind: null }
    : { ...session, ...patch };
}
export type BrowserSessionIntent = {
  requestId: string;
  action: BrowserSessionAction;
  browserId: BrowserId;
  sessionId?: string;
  displayId?: number;
  ownerEpoch: number;
  authScope: DesktopChatAuthScope;
};
export type BrowserSessionSnapshot = {
  owner: string;
  ownerEpoch: number;
  authScope: DesktopChatAuthScope | null;
  revision: number;
  session: BrowserConnectionSession | null;
};
export const useBrowserConnectionSession = create<{
  session: BrowserConnectionSession | null;
}>(() => ({ session: null }));
export const isBrowserConnectAction = (action: BrowserSessionAction) =>
  action === "connect" || action === "restart-connect";
let latestRevision = -1;
let revisionScope: string | null = null;
function synchronizeRevisionScope(): void {
  const auth = getDesktopChatAuthScope();
  const scope = JSON.stringify([
    getCurrentOwnerLabel(),
    getCurrentOwnerEpoch(),
    auth?.accountId,
    auth?.epoch,
  ]);
  if (scope !== revisionScope) {
    revisionScope = scope;
    latestRevision = -1;
  }
}
export const browserSessionUuid = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const browserDisplayId = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= 0 &&
  value <= 0xffffffff;

/** Only an explicit UI action creates executable intent; URLs contain none. */
export async function requestBrowserSessionAction(
  action: BrowserSessionAction,
  browserId: BrowserId,
  sessionId?: string,
  presentation?: { displayId?: number | null },
): Promise<void> {
  const owner = getCurrentOwnerLabel();
  const authScope = getDesktopChatAuthScope();
  if (!isTauri() || !owner || !authScope)
    throw new Error(
      "Browser connection is unavailable. Reopen Settings to try again.",
    );
  if (
    presentation?.displayId != null &&
    !browserDisplayId(presentation.displayId)
  )
    throw new Error("The browser connection display is unavailable.");
  await emitTo(owner, BROWSER_SESSION_ACTION, {
    requestId: crypto.randomUUID(),
    action,
    browserId,
    sessionId,
    ...(presentation?.displayId != null
      ? { displayId: presentation.displayId }
      : {}),
    authScope,
    ownerEpoch: getCurrentOwnerEpoch(),
  } satisfies BrowserSessionIntent);
}

/** A failed delivery remains visible in the common row without creating approval. */
export function reportBrowserSessionRequestError(browserId: BrowserId): void {
  const existing = useBrowserConnectionSession.getState().session;
  if (existing?.busy) return;
  useBrowserConnectionSession.setState({
    session: {
      id: crypto.randomUUID(),
      browserId,
      setupId: null,
      status: null,
      attempt: null,
      busy: null,
      guideLabel: null,
      yielding: false,
      error: translate(desktopBrowserMessages, "errorSessionUnavailable"),
    },
  });
}

export function currentBrowserSessionIntent(
  value: unknown,
): value is BrowserSessionIntent {
  if (!value || typeof value !== "object") return false;
  const request = value as BrowserSessionIntent;
  return (
    browserSessionUuid(request.requestId) &&
    isBrowserId(request.browserId) &&
    [
      "connect",
      "restart-connect",
      "check",
      "disconnect",
      "guide",
      "stop",
    ].includes(request.action) &&
    (request.action !== "restart-connect" || request.browserId === "dia") &&
    (request.displayId === undefined || browserDisplayId(request.displayId)) &&
    (request.sessionId === undefined ||
      browserSessionUuid(request.sessionId)) &&
    (!["guide", "stop"].includes(request.action) ||
      browserSessionUuid(request.sessionId)) &&
    request.ownerEpoch === getCurrentOwnerEpoch() &&
    isCurrentDesktopChatAuthScope(request.authScope)
  );
}

/** Snapshots update presentation only. A mirror never executes a snapshot. */
export function applyBrowserSessionSnapshot(value: unknown): boolean {
  if (
    !value ||
    typeof value !== "object" ||
    getCurrentOwnerLabel() === getMyWindowLabel()
  )
    return false;
  synchronizeRevisionScope();
  const snapshot = value as BrowserSessionSnapshot;
  if (
    snapshot.owner !== getCurrentOwnerLabel() ||
    snapshot.ownerEpoch !== getCurrentOwnerEpoch() ||
    !isCurrentDesktopChatAuthScope(snapshot.authScope) ||
    !Number.isSafeInteger(snapshot.revision) ||
    snapshot.revision <= latestRevision
  )
    return false;
  const session = snapshot.session;
  if (session !== null) {
    if (
      !session ||
      !browserSessionUuid(session.id) ||
      !isBrowserId(session.browserId) ||
      !(session.setupId === null || browserSessionUuid(session.setupId)) ||
      ![null, "setup", "check", "guide", "disconnect"].includes(session.busy) ||
      typeof session.yielding !== "boolean" ||
      (session.yieldId !== undefined && !browserSessionUuid(session.yieldId)) ||
      !(session.error === null || typeof session.error === "string") ||
      !(session.errorKind == null || isErrorKind(session.errorKind)) ||
      !(session.guideLabel === null || typeof session.guideLabel === "string")
    )
      return false;
    try {
      if (
        session.status &&
        parseBrowserConnectionStatus(session.status).browser_id !==
          session.browserId
      )
        return false;
      if (session.attempt) {
        parseBrowserSetupReport(session.attempt.report, session.browserId);
        if (
          !browserSessionUuid(session.attempt.setupId) ||
          !Number.isFinite(session.attempt.completedAt)
        )
          return false;
      }
    } catch {
      return false;
    }
  }
  latestRevision = snapshot.revision;
  useBrowserConnectionSession.setState({ session });
  return true;
}

/** Settings can move to any display; it requests the owner's current receipt. */
export function attachBrowserSessionPresentation(): () => void {
  if (!isTauri()) return () => {};
  let active = true;
  const request = () => {
    synchronizeRevisionScope();
    const owner = getCurrentOwnerLabel();
    const authScope = getDesktopChatAuthScope();
    if (owner && authScope)
      void emitTo(owner, BROWSER_SESSION_REQUEST, {
        requester: getMyWindowLabel(),
        ownerEpoch: getCurrentOwnerEpoch(),
        authScope,
      }).catch(() => {});
  };
  const pending = listen<unknown>(BROWSER_SESSION_STATE, ({ payload }) => {
    if (active) applyBrowserSessionSnapshot(payload);
  });
  void pending
    .then(() => {
      if (active) request();
    })
    .catch(() => {});
  const reset = () => {
    latestRevision = -1;
    useBrowserConnectionSession.setState({ session: null });
    request();
  };
  const stopAuth = subscribeDesktopChatAuthScope(reset);
  const stopOwner = subscribeOwnerChanged(reset);
  return () => {
    active = false;
    stopAuth();
    stopOwner();
    void pending.then(safeTauriUnlisten).catch(() => {});
  };
}
